import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { refreshSettings } from "@/hooks/useSettings";
import { toast } from "sonner";

const AdminPages = () => {
  const [policy, setPolicy] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("settings")
        .select("key, value")
        .in("key", ["privacy_policy", "contact_info"]);
      data?.forEach((row: any) => {
        if (row.key === "privacy_policy") {
          setPolicy(typeof row.value === "string" ? row.value : row.value?.html ?? "");
        }
        if (row.key === "contact_info") {
          setPhone(row.value?.phone ?? "");
          setEmail(row.value?.email ?? "");
          setAddress(row.value?.address ?? "");
        }
      });
      setLoading(false);
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    const payloads = [
      { key: "privacy_policy", value: { html: policy } },
      { key: "contact_info", value: { phone, email, address } },
    ];
    const { error } = await supabase.from("settings").upsert(payloads, { onConflict: "key" });
    setSaving(false);
    if (error) return toast.error(error.message);
    await refreshSettings();
    toast.success("Pages updated");
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Pages</h1>
        <p className="text-sm text-muted-foreground">
          Manage Privacy Policy and Contact information.
        </p>
      </div>

      <Card className="p-6 space-y-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Privacy Policy</h2>
          <p className="text-xs text-muted-foreground">
            Supports HTML (e.g. &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;a&gt;).
          </p>
        </div>
        <Textarea
          value={policy}
          onChange={(e) => setPolicy(e.target.value)}
          rows={14}
          className="font-mono text-xs"
          placeholder="<h2>Privacy Policy</h2>..."
        />
      </Card>

      <Card className="p-6 space-y-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Contact info</h2>
          <p className="text-xs text-muted-foreground">
            Shown on the public Contact page.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="p-phone">Phone</Label>
            <Input id="p-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-email">Email</Label>
            <Input id="p-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="p-addr">Address</Label>
            <Textarea
              id="p-addr"
              rows={3}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button variant="hero" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default AdminPages;
